import React, { useState } from 'react'
import { useTopHottestCardsQuery } from '../../../features/giftCard/cardsSlice'
import { NairaSymbol } from '../svgs/NairaSymbol'
import { Loader } from '../../../shared/Loader'
import { TradeGiftcard } from '../giftcard/TradeGiftcard'
import { TradeType } from '../giftcard/types'

interface HottestCard {
  rank: number
  rate: number
  rankedAt: string
  card: {
    _id: string
    name: string
    image: string
    type: string
    category: any
  }
}

interface TopHottestCardsProps {}

const getRankIcon = (rank: number) => {
  switch (rank) {
    case 1:
      return '🔥'
    case 2:
      return '🥈'
    case 3:
      return '💡'
    case 4:
      return '⭐'
    case 5:
      return '💎'
    case 6:
      return '🎖️'
    case 7:
      return '🏅'
    case 8:
      return '✨'
    case 9:
      return '🚀'
    default:
      return '🎯'
  }
}

const getRankLabel = (rank: number) => {
  switch (rank) {
    case 1:
      return 'HOT PICK'
    case 2:
      return 'Runner-Up'
    case 3:
      return 'Great Deal'
    default:
      return ''
  }
}

export const TopHottestCards: React.FC<TopHottestCardsProps> = () => {
  const { data: hottestCards, isLoading, error } = useTopHottestCardsQuery()
  // Task sheet Oct T13: show 5, reveal the rest on demand.
  const [expanded, setExpanded] = useState(false)
  const [isTradeModalOpen, setIsTradeModalOpen] = useState<boolean>(false)
  const [selectedCard, setSelectedCard] = useState<HottestCard | null>(null)
  
  // Debug log to see the actual data structure
  console.log('Hottest cards data:', hottestCards)

  const handleCardClick = (card: HottestCard) => {
    setSelectedCard(card)
    setIsTradeModalOpen(true)
  }

  if (isLoading) {
    return (
      <div className='bg-black px-4 py-6 rounded-lg'>
        <h3 className='text-primary text-lg font-medium mb-4 flex items-center gap-2'>
          🔥 Top 10 Hottest Gift Cards
        </h3>
        <div className='flex justify-center items-center h-40'>
          <Loader />
        </div>
      </div>
    )
  }

  if (error) {
    console.error('Error fetching hottest cards:', error)
    return (
      <div className='bg-black px-4 py-6 rounded-lg'>
        <h3 className='text-primary text-lg font-medium mb-4 flex items-center gap-2'>
          🔥 Top 10 Hottest Gift Cards
        </h3>
        <div className='text-white/40 text-center py-8'>
          Error loading hottest cards
        </div>
      </div>
    )
  }

  if (!hottestCards) {
    return (
      <div className='bg-black px-4 py-6 rounded-lg'>
        <h3 className='text-primary text-lg font-medium mb-4 flex items-center gap-2'>
          🔥 Top 10 Hottest Gift Cards
        </h3>
        <div className='text-white/40 text-center py-8'>
          No hottest cards available
        </div>
      </div>
    )
  }

  // Get cards from the new data structure (directly an array)
  let cards: HottestCard[] = []
  
  if (hottestCards && Array.isArray(hottestCards)) {
    cards = hottestCards
  }
  const visible = expanded ? cards : cards.slice(0, 5)
  const remaining = cards.length - 5

  return (
    <div className='bg-black px-4 py-6 rounded-lg'>
      <h3 className='text-primary text-lg font-medium mb-4 flex items-center gap-2'>
        💸 Top 10 Hottest Gift Card Rates
      </h3>
      
      {cards.length === 0 ? (
        <div className='text-white/40 text-center py-8'>
          No hottest cards available
        </div>
      ) : (
        <div className='space-y-3'>
          {visible.map((item, index) => (
            <div
              key={item.card._id}
              onClick={() => handleCardClick(item)}
              className={`
                relative overflow-hidden rounded-lg p-3 border transition-all duration-200 hover:transform hover:scale-[1.02] cursor-pointer
                ${item.rank === 1 
                  ? 'bg-gradient-to-r from-red-900/30 to-orange-900/30 border-red-500/50 shadow-red-500/20' 
                  : item.rank === 2
                  ? 'bg-gradient-to-r from-gray-700/30 to-gray-600/30 border-gray-400/50'
                  : item.rank === 3
                  ? 'bg-gradient-to-r from-yellow-900/30 to-amber-900/30 border-yellow-600/50'
                  : 'bg-gray-800/50 border-gray-600/30'
                }
              `}
            >
              {/* Rank badge */}
              <div className='absolute top-2 right-2'>
                <div className={`
                  w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                  ${item.rank === 1 
                    ? 'bg-red-500 text-white' 
                    : item.rank === 2
                    ? 'bg-gray-400 text-black'
                    : item.rank === 3
                    ? 'bg-yellow-500 text-black'
                    : 'bg-gray-600 text-white'
                  }
                `}>
                  {item.rank}
                </div>
              </div>

              <div className='flex items-center gap-3'>
                                 {/* Card image */}
                 <div className='w-12 h-12 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0'>
                   {item.card?.category?.image ? (
                     <img 
                       src={item.card.category.image} 
                       alt={item.card.name}
                       className='w-full h-full object-cover'
                     />
                   ) : (
                     <div className='w-full h-full flex items-center justify-center text-gray-400 text-xs'>
                       {item.card.name.charAt(0)}
                     </div>
                   )}
                 </div>

                {/* Card details */}
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 mb-1'>
                    <span className='text-lg'>{getRankIcon(item.rank)}</span>
                    <h4 className='text-white font-medium text-sm truncate'>
                      {item.card.name}
                    </h4>
                  </div>
                  
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-1'>
                      <NairaSymbol size={12} />
                      <span className='text-primary font-semibold'>
                        {item.rate.toLocaleString()}
                      </span>
                    </div>
                    
                    {getRankLabel(item.rank) && (
                      <span className={`
                        text-xs px-2 py-0.5 rounded-full font-medium
                        ${item.rank === 1 
                          ? 'bg-red-500/20 text-red-300' 
                          : item.rank === 2
                          ? 'bg-gray-500/20 text-gray-300'
                          : 'bg-yellow-500/20 text-yellow-300'
                        }
                      `}>
                        {getRankLabel(item.rank)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Subtle glow effect for top 3 */}
              {item.rank <= 3 && (
                <div className={`
                  absolute inset-0 opacity-10 rounded-lg
                  ${item.rank === 1 
                    ? 'bg-gradient-to-r from-red-500 to-orange-500' 
                    : item.rank === 2
                    ? 'bg-gradient-to-r from-gray-400 to-gray-300'
                    : 'bg-gradient-to-r from-yellow-500 to-amber-500'
                  }
                `} />
              )}
            </div>
          ))}
          {remaining > 0 && (
            <button
              type='button'
              onClick={() => setExpanded((v) => !v)}
              className='w-full mt-2 py-2 rounded-lg border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors'
            >
              {expanded ? 'Show less' : `Load more (${remaining})`}
            </button>
          )}
        </div>
      )}

      {/* Footer note */}
      <div className='mt-4 pt-3 border-t border-gray-700'>
        <p className='text-xs text-gray-400 text-center'>
          Updated hourly • Best rates by category
        </p>
      </div>

      {/* Trade Modal */}
      {selectedCard && (
        <TradeGiftcard
          cards={[{
            _id: selectedCard.card._id,
            name: selectedCard.card.name,
            rate: selectedCard.rate,
            type: TradeType.sell, // Default to sell since these are hottest cards for selling
            category: selectedCard.card.category._id,
            image: selectedCard.card.image,
            isDisabled: false,
            createdAt: new Date().toISOString()
          }]}
          name={selectedCard.card.name}
          isDisabled={false}
          isTradeModalOpen={isTradeModalOpen}
          setIsTradeModalOpen={setIsTradeModalOpen}
          initialCardId={selectedCard.card._id}
          initialRate={selectedCard.rate}
          hideButton={true}
        />
      )}
    </div>
  )
} 